import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, BellRing, Check, Loader2 } from 'lucide-react';
import { NotificationsApi, InboxNotification } from '@/services/school.api';
import { formatDateTime } from '@/lib/format';
import { isPushConfigured, requestWebPushToken, onForegroundPush } from '@/lib/firebase';
import { cn } from '@/lib/cn';

/** Every authenticated user's own inbox — unrelated to /notifications (the admin broadcast history page), which is permission-gated. */
export function NotificationsBell() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [pushState, setPushState] = useState<'idle' | 'enabling' | 'enabled'>(
    typeof window !== 'undefined' && Notification?.permission === 'granted' ? 'enabled' : 'idle',
  );

  const { data: unread } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: NotificationsApi.unreadCount,
    refetchInterval: 30_000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', 'inbox'],
    queryFn: () => NotificationsApi.listInbox({ limit: 10 }),
    enabled: open,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => NotificationsApi.markRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const markAllRead = useMutation({
    mutationFn: NotificationsApi.markAllRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  // Live push while the tab is open — a background message is handled by
  // the service worker instead (see firebase-messaging-sw.js).
  useEffect(() => onForegroundPush(() => qc.invalidateQueries({ queryKey: ['notifications'] })), [qc]);

  const enablePush = async () => {
    setPushState('enabling');
    const token = await requestWebPushToken();
    if (token) {
      await NotificationsApi.registerPushToken(token);
      setPushState('enabled');
    } else {
      setPushState('idle');
    }
  };

  const openItem = async (n: InboxNotification) => {
    if (!n.isRead) markRead.mutate(n.id);
    setOpen(false);
    if (n.actionUrl) navigate(n.actionUrl);
  };

  const count = unread?.count ?? 0;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
        title="Notifications"
      >
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-20 mt-1 w-96 rounded-md border border-slate-200 bg-white shadow-lg">
            <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
              <span className="text-sm font-semibold text-slate-800">Notifications</span>
              {count > 0 && (
                <button
                  onClick={() => markAllRead.mutate()}
                  disabled={markAllRead.isPending}
                  className="flex items-center gap-1 text-xs font-medium text-brand-700 hover:text-brand-800 disabled:opacity-60"
                >
                  <Check className="h-3.5 w-3.5" />
                  Mark all read
                </button>
              )}
            </div>

            {isPushConfigured() && pushState !== 'enabled' && (
              <button
                onClick={enablePush}
                disabled={pushState === 'enabling'}
                className="flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2 text-left text-xs text-brand-700 hover:bg-brand-50 disabled:opacity-60"
              >
                {pushState === 'enabling' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BellRing className="h-3.5 w-3.5" />}
                Enable browser notifications
              </button>
            )}

            <div className="max-h-96 overflow-y-auto">
              {isLoading ? (
                <div className="px-3 py-6 text-center text-sm text-slate-400">Loading…</div>
              ) : !data?.items.length ? (
                <div className="px-3 py-6 text-center text-sm text-slate-400">No notifications yet.</div>
              ) : (
                data.items.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => openItem(n)}
                    className={cn(
                      'flex w-full flex-col items-start gap-0.5 border-b border-slate-50 px-3 py-2.5 text-left last:border-b-0 hover:bg-slate-50',
                      !n.isRead && 'bg-brand-50/40',
                    )}
                  >
                    <div className="flex w-full items-center gap-1.5">
                      {!n.isRead && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-600" />}
                      <span className="truncate text-sm font-medium text-slate-900">{n.title}</span>
                    </div>
                    <span className="line-clamp-2 text-xs text-slate-600">{n.message}</span>
                    <span className="text-[10px] text-slate-400">{formatDateTime(n.createdAt)}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
